import { getAcceptFile } from 'app/utils/files';
import { useTranslate } from 'core/hooks/useTranslate';
import { Icon } from 'ui';
import React from 'react';
import { useToasts } from 'react-toast-notifications';

type DropzoneProps = {
  accept?: '' | 'image' | 'audio' | 'video' | 'zip';
  accepts?: string;
  disabled?: boolean;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  isMulti?: boolean;
  mimeType?: string[];
  onSelectFiles?: (files: File[]) => void;
  openFileDialog?: () => void;
  subTitle?: string;
  title?: string;
};

function Dropzone({
  accept = '',
  accepts,
  disabled = false,
  fileInputRef,
  isMulti = false,
  mimeType = [],
  onSelectFiles = () => null,
  openFileDialog,
  subTitle = '',
  title = '',
}: DropzoneProps): JSX.Element {
  const { t } = useTranslate();
  const { addToast } = useToasts();
  const [typeMedia, setTypeMedia] = React.useState<string[]>([]);
  const [hightlight, setHightlight] = React.useState<boolean>(false);
  const internalFileInputRef = React.createRef<HTMLInputElement>();
  const actualFileInputRef = fileInputRef || internalFileInputRef;

  React.useEffect(getAccept, [accept]);
  React.useEffect(getMimeType, [mimeType]);

  function handleOpenFileDialog(): void {
    if (disabled) return;
    if (openFileDialog) {
      openFileDialog();
    } else if (actualFileInputRef.current) {
      actualFileInputRef.current.click();
    }
  }

  function onChange(event: React.ChangeEvent<HTMLInputElement>): void {
    if (disabled) return;

    const files = event.target.files;
    if (!files) return;

    if (mimeType.length && !mimeType.find((item) => item === files[0].type)) {
      addToast(t('common:File format incorrect'), { appearance: 'warning' });
      return;
    }

    const array = fileListToArray(files);
    onSelectFiles(array);
  }

  function onClickInputFile(event: React.MouseEvent<HTMLInputElement>): void {
    (event.target as HTMLInputElement).value = '';
  }

  function onDragOver(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (disabled) return;
    setHightlight(true);
  }

  function onDragLeave(): void {
    setHightlight(false);
  }

  function onDrop(event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    if (disabled) return;

    const files = event.dataTransfer.files;
    const array = fileListToArray(files);
    onSelectFiles(array);
    setHightlight(false);
  }

  function fileListToArray(list: FileList): File[] {
    const array: File[] = [];
    for (let i = 0; i < list.length; i++) {
      const file = list.item(i);
      if (file) {
        array.push(file);
      }
    }

    return array;
  }

  function getAccept(): void {
    if (accept) {
      const types = getAcceptFile(accept);
      setTypeMedia(types);
    }
  }

  function getMimeType(): void {
    if (mimeType.length) {
      setTypeMedia(mimeType);
    }
  }

  function getClassName(): string {
    const isDropzone = hightlight ? 'dropzone__highlight' : '';
    const isDisabled = disabled ? 'disabled' : '';

    return `${isDropzone} ${isDisabled}`;
  }

  return (
    <div className="dropzone">
      <span className="title">{title}</span>
      <div className={`dropzone-box ${getClassName()}`} onClick={handleOpenFileDialog} onDragLeave={onDragLeave} onDragOver={onDragOver} onDrop={onDrop}>
        <div className="mb-3">
          <Icon name="upload" size="xl" color="default" />
        </div>

        <input
          accept={accepts ? accepts : typeMedia.join(',')}
          className="file-input"
          multiple={isMulti}
          onChange={onChange}
          onClick={onClickInputFile}
          ref={actualFileInputRef}
          type="file"
        />

        <div className="select-file">
          <span>{t('common:Select file')}</span>
        </div>
      </div>
      <span className="sub-title">{subTitle}</span>
    </div>
  );
}

export default Dropzone;
